import { App as CapApp } from '@capacitor/app';
import { type ReactNode, createContext, useCallback, useContext, useEffect, useRef } from 'react';

/**
 * D-pad navigation.
 *
 * A TV remote has four arrows, OK and Back — no pointer, no tab order that
 * anyone can see. So the app has to answer "what is to the right of this?"
 * itself, and it has to keep answering correctly while the layout reflows.
 *
 * Two decisions shape everything below:
 *
 *  1. **The DOM is the registry.** Anything reachable opts in with
 *     `data-focusable`; candidates are queried at the moment a key is pressed.
 *     No registration, no stale entries when a list re-renders or a dialog
 *     unmounts, and conditional rendering needs no special handling.
 *  2. **Focus is real DOM focus.** Focusables are real `<button>`s, so OK is
 *     just Enter/Space on a button and a tablet tap is just a click. The TV
 *     path and the touch path are the same code path, which is why supporting
 *     both costs nothing here.
 */

import { type Box, type Direction, chooseInDirection } from './geometry';

export type { Direction };

const FOCUSABLE = '[data-focusable]:not([disabled]):not([hidden]):not([aria-hidden="true"])';

/** Zero-sized elements are `display:none` or collapsed — unreachable either way. */
function isVisible(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

function focusablesWithin(root: ParentNode): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(isVisible);
}

function boxOf(element: HTMLElement): Box {
  const { left, top, right, bottom } = element.getBoundingClientRect();
  return { left, top, right, bottom };
}

function pickNeighbour(
  current: HTMLElement,
  candidates: HTMLElement[],
  direction: Direction,
): HTMLElement | null {
  return chooseInDirection(
    boxOf(current),
    candidates
      .filter((element) => element !== current)
      .map((element) => ({ item: element, box: boxOf(element) })),
    direction,
  );
}

export interface TvNavigation {
  /** Moves focus one step. Returns false when there was nowhere to go. */
  moveFocus(direction: Direction): boolean;
  /** Focuses the first focusable in the active scope. */
  focusFirst(): boolean;
  /**
   * Confines navigation to `element` until the returned function is called.
   * Dialogs use this so the D-pad cannot wander back to the page behind them.
   */
  pushScope(element: HTMLElement): () => void;
  /**
   * Handles Back until the returned function is called. The most recently
   * pushed handler wins, which is what makes "Back closes the dialog, then
   * Back leaves the screen" fall out for free.
   */
  pushBackHandler(handler: () => void): () => void;
}

const TvNavigationContext = createContext<TvNavigation | null>(null);

export function useTvNavigation(): TvNavigation {
  const value = useContext(TvNavigationContext);
  if (!value) throw new Error('useTvNavigation used outside <TvNavigationProvider>.');
  return value;
}

function directionFor(key: string): Direction | null {
  switch (key) {
    case 'ArrowUp':
    case 'Up':
      return 'up';
    case 'ArrowDown':
    case 'Down':
      return 'down';
    case 'ArrowLeft':
    case 'Left':
      return 'left';
    case 'ArrowRight':
    case 'Right':
      return 'right';
    default:
      return null;
  }
}

/** Text fields keep the arrows that move a caret; the rest belong to the D-pad. */
function isTextEntry(element: Element | null): element is HTMLInputElement | HTMLTextAreaElement {
  if (!element) return false;
  const tag = element.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || (element as HTMLElement).isContentEditable;
}

export function TvNavigationProvider({ children }: { children: ReactNode }) {
  const scopes = useRef<HTMLElement[]>([]);
  const backHandlers = useRef<(() => void)[]>([]);

  const activeRoot = useCallback((): ParentNode => {
    const top = scopes.current[scopes.current.length - 1];
    return top && top.isConnected ? top : document;
  }, []);

  const focusFirst = useCallback((): boolean => {
    const [first] = focusablesWithin(activeRoot());
    if (!first) return false;
    first.focus();
    return true;
  }, [activeRoot]);

  const moveFocus = useCallback(
    (direction: Direction): boolean => {
      const root = activeRoot();
      const candidates = focusablesWithin(root);
      if (candidates.length === 0) return false;

      const active = document.activeElement as HTMLElement | null;
      // Nothing focused yet (fresh screen, or focus fell out from under us when
      // its element unmounted): the first press should land somewhere sensible
      // rather than being swallowed.
      const inScope =
        active && active !== document.body && candidates.includes(active) ? active : null;
      if (!inScope) {
        const [first] = candidates;
        if (!first) return false;
        first.focus();
        return true;
      }

      const next = pickNeighbour(inScope, candidates, direction);
      if (!next) return false;
      next.focus();
      return true;
    },
    [activeRoot],
  );

  const pushScope = useCallback((element: HTMLElement): (() => void) => {
    scopes.current = [...scopes.current, element];
    return () => {
      scopes.current = scopes.current.filter((scope) => scope !== element);
    };
  }, []);

  const pushBackHandler = useCallback((handler: () => void): (() => void) => {
    backHandlers.current = [...backHandlers.current, handler];
    return () => {
      backHandlers.current = backHandlers.current.filter((entry) => entry !== handler);
    };
  }, []);

  const goBack = useCallback((): boolean => {
    const handler = backHandlers.current[backHandlers.current.length - 1];
    if (!handler) return false;
    handler();
    return true;
  }, []);

  // Which input device is driving. The focus ring is loud on a TV and unwanted
  // on a tablet, so the CSS keys off this instead of `:focus-visible`, whose
  // heuristics do not know that a D-pad is a keyboard.
  useEffect(() => {
    const root = document.documentElement;
    const setMode = (mode: 'key' | 'pointer') => {
      if (root.dataset.input !== mode) root.dataset.input = mode;
    };
    setMode('pointer');

    const onKeyDown = (event: KeyboardEvent) => {
      if (directionFor(event.key) || event.key === 'Enter') setMode('key');
    };
    const onPointerDown = () => setMode('pointer');

    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.metaKey || event.altKey || event.ctrlKey) return;

      const target = document.activeElement;
      const direction = directionFor(event.key);

      if (direction) {
        // Inside a text field, left/right are the caret's. Up/down still leave
        // the field, which is the only way off it with a remote.
        if (isTextEntry(target) && (direction === 'left' || direction === 'right')) return;
        if (moveFocus(direction)) event.preventDefault();
        return;
      }

      // Escape is Back everywhere. Backspace is Back too — except in a text
      // field, where deleting a character is obviously what was meant.
      const isBackKey =
        event.key === 'Escape' ||
        event.key === 'GoBack' ||
        event.key === 'BrowserBack' ||
        (event.key === 'Backspace' && !isTextEntry(target));

      if (isBackKey) {
        // First Escape in a text field just leaves the field.
        if (event.key === 'Escape' && isTextEntry(target)) {
          (target as HTMLElement).blur();
          event.preventDefault();
          return;
        }
        if (goBack()) event.preventDefault();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [goBack, moveFocus]);

  // Android's hardware Back is not a key event inside a WebView — Capacitor
  // surfaces it as its own event, and without this it would close the app from
  // any screen.
  useEffect(() => {
    const listener = CapApp.addListener('backButton', () => {
      if (!goBack()) void CapApp.exitApp();
    });
    return () => {
      void listener.then((handle) => handle.remove());
    };
  }, [goBack]);

  const value = useRef<TvNavigation>({
    moveFocus,
    focusFirst,
    pushScope,
    pushBackHandler,
  });
  value.current = { moveFocus, focusFirst, pushScope, pushBackHandler };

  return (
    <TvNavigationContext.Provider value={value.current}>{children}</TvNavigationContext.Provider>
  );
}
